// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createContext, useState, useMemo } from "react";

import { store } from "./slices/store";

import { asgardeoConfig } from "./config/config";
import AppHandler from "@app/AppHandler";
import { themeSettings } from "./theme";
import "./App.scss";

import { Provider } from "react-redux";
import AuthGuard from "@app/AuthGuard";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { AsgardeoProvider } from "@asgardeo/react";
import { SnackbarProvider } from "notistack";

export const ColorModeContext = createContext({ toggleColorMode: () => {} });

function App() {
  const pathName = window.location.pathname;
  const search = window.location.search;
  const match = pathName.match(/^\/interview\/([^/]+)\/feedback\/?$/);
  if (match) {
    const interviewId = match[1];
    sessionStorage.setItem("feedbackId", interviewId);
  }

  if (pathName !== "/" && !match) {
    const fullPath = pathName + search;
    localStorage.setItem("internal-app-redirect-url", fullPath);
  }

  const processLocalThemeMode = (): "light" | "dark" => {
    var localMode: string | null = localStorage.getItem("internal-app-theme");

    if (localMode && ["light", "dark"].indexOf(localMode) > -1) {
      return ["light", "dark"].indexOf(localMode) === 0 ? "light" : "dark";
    } else {
      localStorage.setItem("internal-app-theme", "light");
      return "light";
    }
  };

  const [mode, setMode] = useState<"light" | "dark">(processLocalThemeMode());

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        localStorage.setItem(
          "internal-app-theme",
          mode === "light" ? "dark" : "light"
        );
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
    }),
    [mode]
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <SnackbarProvider maxSnack={3} preventDuplicate>
        <ThemeProvider theme={theme}>
          <Provider store={store}>
            <AsgardeoProvider
              baseUrl={asgardeoConfig.baseUrl}
              clientId={asgardeoConfig.clientId}
              afterSignInUrl={asgardeoConfig.afterSignInUrl}
              afterSignOutUrl={asgardeoConfig.afterSignOutUrl}
              scopes={asgardeoConfig.scopes}
              preferences={{
                user: {
                  fetchUserProfile: false,
                  fetchOrganizations: false,
                },
              }}
            >
              <AuthGuard>
                <AppHandler />
              </AuthGuard>
            </AsgardeoProvider>
          </Provider>
        </ThemeProvider>
      </SnackbarProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
